import { ActionIcon, Stack, Text, Tooltip } from "@mantine/core";
import { modals } from "@mantine/modals";
import { GridColDef } from "@mui/x-data-grid";
import { CheckIcon } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";

import { DataGridBase } from "@/components/DataGridBase";
import { Plate } from "@/components/Plate";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { QueueRestore, Role, useGetQueueRestoresQuery, useSaveQueueRestoreMutation } from "@/store";
import { cn } from "@/utils";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_base/resgate/")({
  component: RouteComponent,
  beforeLoad: requireRoles({
    allowed: [Role.Administrador]
  })
});

function RouteComponent() {
  const { data = [], isFetching, refetch } = useGetQueueRestoresQuery();
  const [trigger, { isLoading }] = useSaveQueueRestoreMutation();

  const columns: GridColDef<QueueRestore>[] = [
    { field: "id", headerName: "ID", width: 75, align: "center", headerAlign: "center", type: "number" },
    {
      field: "plate",
      valueGetter: (val, { truck }) => truck.licensePlate,
      headerName: "Placa",
      flex: 1,
      minWidth: 175,
      type: "custom",
      display: "flex",
      renderCell: ({ row }) => {
        const { truck } = row;
        return <Plate plate={truck.licensePlate} />;
      }
    },
    {
      field: "driver",
      valueGetter: (val, { driver }) => driver.name,
      headerName: "Motorista",
      display: "flex",
      flex: 1,
      type: "custom",
      minWidth: 250,
      renderCell: ({ row }) => {
        const { driver, qtyTransported } = row;
        return (
          <div className="flex flex-col">
            <Text>{driver.name}</Text>
            <Text size="xs" c="dimmed" fw="bold">
              Peso: {qtyTransported}
            </Text>
          </div>
        );
      }
    },
    {
      field: "company",
      valueGetter: (val, { company }) => company.name,
      headerName: "Transportadora",
      flex: 1,
      type: "string",
      minWidth: 150
    },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      minWidth: 150,
      renderCell: ({ row }) => {
        const { id, status, company, truck, qtyTransported, order } = row;

        if (status) return null;

        const handleApproveRescue = async () => {
          modals.openConfirmModal({
            title: "Resgate",
            children: (
              <>
                <Plate plate={truck.licensePlate} color="text-blue-500" />
                <div className="mt-2">
                  <Text size="lg">Peso: {qtyTransported}</Text>
                  <Text size="lg">Pedido: {order.code}</Text>
                </div>
              </>
            ),
            groupProps: {
              className: cn("grid! grid-cols-1 gap-2 md:grid-cols-2")
            },
            confirmProps: {
              color: "green",
              loading: isLoading
            },
            cancelProps: {
              variant: "default",
              disabled: isLoading
            },
            labels: {
              cancel: "Cancelar",
              confirm: "Resgatar"
            },
            onConfirm: () => {
              trigger({
                id,
                company: {
                  id: String(company.id)
                }
              });
            }
          });
        };

        return (
          <div className="flex gap-2">
            <Tooltip label="Aprovar resgate">
              <ActionIcon color="green" variant="light" radius="xl" onClick={handleApproveRescue}>
                <CheckIcon weight="bold" />
              </ActionIcon>
            </Tooltip>
          </div>
        );
      }
    }
  ];

  return (
    <Stack>
      <div className="flex items-center justify-between">
        <DashboardComponents.Title>Resgates</DashboardComponents.Title>

        <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Resgates" }} onClick={refetch} loading={isFetching} />
      </div>

      <DataGridBase rows={data} columns={columns} loading={isFetching} />

      {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
    </Stack>
  );
}
